import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { ArrowRight, ArrowLeft } from 'lucide-react'
import type { FormData } from '@/hooks/useSubmissionWizard'

interface NameStepProps {
  formData: FormData
  updateField: <K extends keyof FormData>(field: K, value: FormData[K]) => void
  onNext: () => void
  onBack: () => void
  canGoBack: boolean
}

export function NameStep({ formData, updateField, onNext, onBack, canGoBack }: NameStepProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 100)
    return () => clearTimeout(timer)
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim()) {
      setError('Please enter your name')
      return
    }
    onNext()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && formData.name.trim()) {
      e.preventDefault()
      onNext()
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mx-auto max-w-xl space-y-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
        <h2 className="text-foreground font-display text-3xl font-bold md:text-4xl lg:text-5xl">What's your name?</h2>
        <p className="text-foreground-muted text-lg">This will appear alongside your submission</p>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Input
          ref={inputRef}
          type="text"
          placeholder="Your full name"
          value={formData.name}
          onChange={(e) => {
            setError(null)
            updateField('name', e.target.value)
          }}
          onKeyDown={handleKeyDown}
          error={error ?? undefined}
          className="text-xl"
        />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="flex items-center gap-4"
      >
        {canGoBack && (
          <Button type="button" variant="ghost" size="lg" onClick={onBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        <Button
          type="submit"
          size="lg"
          disabled={!formData.name.trim()}
          className="flex-1 md:min-w-[200px] md:flex-none"
        >
          Continue
          <ArrowRight className="h-5 w-5" />
        </Button>
        <span className="text-foreground-subtle hidden text-sm md:block">or press Enter</span>
      </motion.div>
    </form>
  )
}
